import { Avatar, Grid, List, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import { Box } from "@mui/system";
import ThemeContext from "components/app/Context";
import Button from "components/common/Button";
import Feedback from "components/common/Feedback";
import Spinner from "components/common/Spinner/Spinner";
import Typography from "components/common/Typography";
import UserAvatarList from "components/common/UserAvatarList";
import AvatarItem from "components/common/UserAvatarList/item";
import { sanitize } from "dompurify";
import { uniq } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes, regExpForId } from "utils/CommonConstants";
import { getUserName } from "utils/CommonFunctions";
import {
  fetchAssigneeDetailsAsync,
  fetchCommentUsersAsync,
  fetchPlanMembersAsync
} from "../MyPlanDetails/MyPlanDetails.slice";
import { createCommentAsync, fetchCommentAsync } from "../planDetails/PlanDetail.slice";
import "./Comments.scss";
import { MentionInput } from "./MentionInput";

export const Comments = () => {
  const commentUsers: any = useAppSelector((state) => state.myPlanDetails.commentUsers);
  const commentLists: any = useAppSelector((state) => state.planDetails.commentList);
  const activeCard: any = useAppSelector((state) => state.app.activeActionCard);
  const planMembersDetail: any = useAppSelector((state) => state.myPlanDetails.planMembers);
  const commentShowLoader: boolean | undefined = useAppSelector((state) => state.planDetails.commentShowLoader);
  const { isMobileScreen } = useContext(ThemeContext);
  const { sectionId, cardId, planId } = activeCard;
  const [commentValue, setCommentValue] = useState<any>();
  const allUsers: any = [];
  const dispatch = useAppDispatch();
  const regExpForStr = /\[{~(.*?)\~}]/g;
  const regExpForComment = /\[~~(.*?)\~~]/g;

  useEffect(() => {
    dispatch(fetchCommentAsync({ sectionId, cardId, planId }));
  }, []);

  useEffect(() => {
    commentLists.forEach((commUser: any) => {
      allUsers.push(commUser.createdBy);
      if (commUser?.taggedUserIds?.length) {
        allUsers.push(...commUser.taggedUserIds);
      }
    });
    const uniqueUsers = uniq(allUsers);
    if (uniqueUsers.length) {
      const data = {
        value: uniqueUsers,
        size: uniqueUsers.length
      };
      dispatch(fetchCommentUsersAsync(data));
      dispatch(fetchPlanMembersAsync({ planId: planId }));
    }
  }, [commentLists]);

  useEffect(() => {
    let membersList = uniq(planMembersDetail?.map((item: any) => item.hltId));
    if (membersList?.length) {
      const data = {
        value: membersList,
        size: membersList?.length
      };
      dispatch(fetchAssigneeDetailsAsync(data));
    }
  }, [planMembersDetail]);

  const postComment = () => {
    const matches = commentValue.match(regExpForId) || [];
    let finalStr = commentValue.replace(regExpForStr, "");

    const idArr = matches.map((match: string) => match.replace(/\[~~|\~~\]/g, ""));
    const data = {
      sectionId,
      cardId,
      planId,
      comments: finalStr,
      taggedUserIds: idArr
    };
    dispatch(createCommentAsync(data));
    setCommentValue("");
  };

  const getReplacedComment = (id: any) => {
    const currId = id.replace("[~~", "").replace("~~]", "");
    const user = commentUsers?.find((u: any) => u.hltId === currId);
    return `<b>@${user?.firstName ? user?.firstName : ""} ${user?.lastName ? user?.lastName : ""}</b>`;
  };

  return (
    <>
      <Feedback form={"addComment"} />

      <Grid container display="flex" flexDirection="column" sx={{ padding: isMobileScreen ? "24px" : "32px" }}>
        {commentShowLoader && (
          <Box
            sx={{
              width: "100%",
              minHeight: "180px",
              alignItems: "center",
              justifyContent: "center",
              display: "flex"
            }}
          >
            <Spinner />
          </Box>
        )}
        {!commentShowLoader && (
          <>
            {commentLists && (
              <Typography color={tokens.Text.General.PrimaryBody} showBold>
                {commentLists.length} Comments
              </Typography>
            )}
            <List
              sx={{
                display: "flex",
                overflow: "auto",
                margin: "10px 0 15px",
                flexDirection: "column",
                minHeight: "120px"
              }}
            >
              {commentLists?.map((comment: any) => {
                if (commentUsers.length) {
                  const currUser = commentUsers.find((u: any) => u.hltId === comment.createdBy);
                  const userName = `${currUser?.firstName ? currUser?.firstName : ""} ${
                    currUser?.lastName ? currUser?.lastName : ""
                  }`;
                  const replacedComment = comment.comments.replace(regExpForComment, getReplacedComment);
                  return (
                    <Box
                      sx={{ paddingBottom: "16px", borderBottom: `1px solid ${tokens.Border.General.Tertiary}` }}
                      key={comment.created}
                    >
                      <ListItem sx={{ paddingLeft: 0, paddingRight: 0, margin: 0 }}>
                        <ListItemAvatar>
                          {currUser && (
                            <Avatar
                              className="avatar-list"
                              sx={{ width: "46px", height: "46px", backgroundColor: "unset" }}
                            >
                              <UserAvatarList users={[currUser]} />
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography showBold>{userName}</Typography>}
                          secondary={comment.created}
                        />
                      </ListItem>
                      <Typography>
                        <span dangerouslySetInnerHTML={{ __html: sanitize(replacedComment) }}></span>
                      </Typography>
                    </Box>
                  );
                }

                return null;
              })}
            </List>
          </>
        )}
        <Box
          sx={{
            width: "100%",
            position: "sticky",
            bottom: 0,
            backgroundColor: `${tokens.Background.General.Primary}`,
            left: 0,
            zIndex: 2
          }}
        >
          <Box sx={{ display: "flex" }}>
            <AvatarItem size="regular" variant="colored" user={getUserName(true, true)} />
            <MentionInput commentValue={commentValue} setCommentValue={setCommentValue} />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "15px",
              marginBottom: "16px"
            }}
          >
            <Button
              variant="primary"
              label="Submit"
              width="160px"
              accessType={accessTypes.update}
              onClick={postComment}
            />
          </Box>
        </Box>
      </Grid>
    </>
  );
};
